/**
 * TELEGRAM-GROWTH-R1 — Customer-side start_param reader + dispatcher (mirrors Worker SoT).
 * Keep in sync with apps/workers-api/src/lib/telegram-growth-r1/startapp-dispatcher.ts
 */

export type StartParamKind =
  | "shop"
  | "orders"
  | "referral"
  | "docsret"
  | "order"
  | "payret"
  | "refundret"
  | "payment_reminder"
  | "product"
  | "category"
  | "campaign"
  | "organic"
  | "unknown";

export type GrowthAttributionFields = {
  source: string;
  medium: string;
  campaign: string | null;
  placement: string | null;
  start_param: string | null;
};

export type DispatchedStartParam = {
  kind: StartParamKind;
  raw: string;
  route_store_home: boolean;
  attribution: GrowthAttributionFields;
  payload: string | null;
};

const MAX = 64;

export function normalizeGrowthToken(raw: string, maxLen = MAX): string {
  return String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, maxLen);
}

function organic(startParam: string | null): GrowthAttributionFields {
  return {
    source: "direct",
    medium: "organic",
    campaign: null,
    placement: null,
    start_param: startParam,
  };
}

export function mapShopStartParamToAttribution(raw: string): GrowthAttributionFields {
  const token = normalizeGrowthToken(raw);
  if (!token || token === "shop") {
    return { source: "shop", medium: "telegram", campaign: null, placement: null, start_param: token || "shop" };
  }
  const body = token.startsWith("shop_") ? token.slice(5) : token;
  if (body.startsWith("launch_")) {
    return { source: "shop", medium: "telegram", campaign: body, placement: null, start_param: token };
  }
  const parts = body.split("_").filter(Boolean);
  const known = new Set(["banner", "instagram", "website", "qr", "email", "print", "ads", "facebook", "tiktok"]);
  const source = parts[0] || "shop";
  if (parts.length <= 1) {
    return { source: known.has(source) ? source : source, medium: "telegram", campaign: null, placement: null, start_param: token };
  }
  if (parts.length === 2) {
    return {
      source: known.has(source) ? source : source,
      medium: "telegram",
      campaign: parts[1] ?? null,
      placement: null,
      start_param: token,
    };
  }
  return {
    source: known.has(source) ? source : source,
    medium: "telegram",
    campaign: parts.slice(1, -1).join("_") || null,
    placement: parts[parts.length - 1] ?? null,
    start_param: token,
  };
}

export function dispatchStartParam(rawInput: string | null | undefined): DispatchedStartParam {
  const raw = String(rawInput ?? "").trim().slice(0, MAX);
  if (!raw) {
    return { kind: "organic", raw: "", route_store_home: true, attribution: organic(null), payload: null };
  }
  const lower = raw.toLowerCase();
  try {
    if (lower === "orders") {
      return { kind: "orders", raw, route_store_home: false, attribution: organic(raw), payload: null };
    }
    if (lower.startsWith("ref_")) {
      return {
        kind: "referral",
        raw,
        route_store_home: true,
        attribution: { source: "referral", medium: "telegram", campaign: null, placement: null, start_param: lower },
        payload: raw.slice(4).trim() || null,
      };
    }
    if (lower.startsWith("docsret_")) {
      return { kind: "docsret", raw, route_store_home: false, attribution: organic(raw), payload: raw.slice(8).trim() || null };
    }
    if (lower.startsWith("order_")) {
      return { kind: "order", raw, route_store_home: false, attribution: organic(raw), payload: raw.slice(6).trim() || null };
    }
    if (lower.startsWith("payret") || lower.startsWith("refundret") || lower.startsWith("rmpay_") || lower.startsWith("rmord_")) {
      return {
        kind: lower.startsWith("refundret") ? "refundret" : lower.startsWith("payret") ? "payret" : "payment_reminder",
        raw,
        route_store_home: false,
        attribution: organic(raw),
        payload: raw,
      };
    }
    if (lower.startsWith("product_")) {
      return {
        kind: "product",
        raw,
        route_store_home: false,
        attribution: { source: "product_link", medium: "telegram", campaign: null, placement: null, start_param: lower },
        payload: raw.slice(8).trim() || null,
      };
    }
    if (lower.startsWith("category_")) {
      return {
        kind: "category",
        raw,
        route_store_home: false,
        attribution: { source: "category_link", medium: "telegram", campaign: null, placement: null, start_param: lower },
        payload: raw.slice(9).trim() || null,
      };
    }
    if (lower.startsWith("campaign_")) {
      const code = normalizeGrowthToken(raw.slice(9)) || null;
      return {
        kind: "campaign",
        raw,
        route_store_home: true,
        attribution: { source: "campaign", medium: "telegram", campaign: code, placement: null, start_param: lower },
        payload: code,
      };
    }
    if (lower === "shop" || lower.startsWith("shop_")) {
      return {
        kind: "shop",
        raw,
        route_store_home: true,
        attribution: mapShopStartParamToAttribution(lower),
        payload: null,
      };
    }
    if (/^[a-z0-9_]{1,64}$/i.test(raw)) {
      const token = normalizeGrowthToken(raw);
      return {
        kind: "unknown",
        raw,
        route_store_home: true,
        attribution: { source: "unknown", medium: "telegram", campaign: token || null, placement: null, start_param: token },
        payload: token,
      };
    }
  } catch {
    /* soft */
  }
  return {
    kind: "unknown",
    raw,
    route_store_home: true,
    attribution: organic(normalizeGrowthToken(raw) || null),
    payload: null,
  };
}

/** Trusted Telegram start_param: initDataUnsafe → tgWebAppStartParam → start_param. */
export function readLaunchStartParam(): string {
  try {
    const unsafe = (
      window as unknown as {
        Telegram?: { WebApp?: { initDataUnsafe?: { start_param?: string } } };
      }
    ).Telegram?.WebApp?.initDataUnsafe?.start_param;
    if (typeof unsafe === "string" && unsafe.trim()) return unsafe.trim().slice(0, MAX);
  } catch {
    /* ignore */
  }
  try {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get("tgWebAppStartParam") ?? params.get("start_param");
    if (fromQuery?.trim()) return fromQuery.trim().slice(0, MAX);
  } catch {
    /* ignore */
  }
  return "";
}

export async function attributeGrowthStartapp(
  sessionKey: string,
  startParam: string | null | undefined,
  telegramUserId?: string | null,
): Promise<boolean> {
  try {
    const res = await fetch("/api/store-v2/growth-attribute", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        session_key: sessionKey,
        startapp: startParam ?? "",
        start_param: startParam ?? "",
        telegram_user_id: telegramUserId ?? null,
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}
